import type { TFunction } from 'i18next';

/**
 * Ce qu'une impression demandée depuis le mobile serveur est devenue, dit au
 * serveur dans les termes de SA sortie ticket.
 *
 * Extrait de `TicketFooter.tsx` — qui portait déjà ce bloc en double avec
 * `TablesOverview.tsx` et le signalait comme dette — au moment où SC-960 a
 * donné un « Imprimer » aux commandes web : trois copies d'une même table de
 * messages divergent à la première sortie ajoutée d'un seul côté.
 *
 * Les clés `waiter.overview.printTotal*` sont inchangées : la copie reste
 * identique d'une surface à l'autre.
 */
export type PrintOutcomeDestination = 'clodop' | 'caisse' | 'cloud';

export const COUNTER_PRINT_POLL_MS = 1000;

export const PRINT_OUTCOME_TIMEOUT_MS: Record<PrintOutcomeDestination, number> = {
  clodop: 8000,
  caisse: 12000,
  cloud: 12000,
};

export interface PrintOutcomeMessages {
  sent: string;
  printed: string;
  failed: string;
  none: string;
}

export const PRINT_OUTCOME_MESSAGES: Record<PrintOutcomeDestination, PrintOutcomeMessages> = {
  clodop: {
    sent: 'waiter.overview.printTotalRelayed',
    printed: 'waiter.overview.printTotalPrinted',
    failed: 'waiter.overview.printTotalFailed',
    none: 'waiter.overview.printTotalNoCounter',
  },
  caisse: {
    sent: 'waiter.overview.printTotalRelayedCaisse',
    printed: 'waiter.overview.printTotalPrintedCaisse',
    failed: 'waiter.overview.printTotalFailedCaisse',
    none: 'waiter.overview.printTotalNoCaisse',
  },
  cloud: {
    sent: 'waiter.overview.printTotalRelayedCloud',
    printed: 'waiter.overview.printTotalPrintedCloud',
    failed: 'waiter.overview.printTotalFailedCloud',
    none: 'waiter.overview.printTotalNoCloud',
  },
};

/**
 * SC-1038 — la même mécanique, dite du BON DE CUISINE. Les messages
 * ci-dessus nomment tous « l'addition » : un serveur qui vient de renvoyer un
 * bon et lit « Addition imprimée au comptoir » va vérifier le mauvais papier,
 * et sur le mauvais appareil si le comptoir n'est pas la cuisine. Seule la
 * table de copie change ; le sondage, les délais et les états restent les
 * mêmes, sans quoi ce serait la quatrième copie du bloc que ce fichier existe
 * pour supprimer.
 */
export const KITCHEN_REPRINT_OUTCOME_MESSAGES: Record<
  PrintOutcomeDestination,
  PrintOutcomeMessages
> = {
  clodop: {
    sent: 'waiter.ticket.commandes.reprintKitchen.outcome.clodop.sent',
    printed: 'waiter.ticket.commandes.reprintKitchen.outcome.clodop.printed',
    failed: 'waiter.ticket.commandes.reprintKitchen.outcome.clodop.failed',
    none: 'waiter.ticket.commandes.reprintKitchen.outcome.clodop.none',
  },
  caisse: {
    sent: 'waiter.ticket.commandes.reprintKitchen.outcome.caisse.sent',
    printed: 'waiter.ticket.commandes.reprintKitchen.outcome.caisse.printed',
    failed: 'waiter.ticket.commandes.reprintKitchen.outcome.caisse.failed',
    none: 'waiter.ticket.commandes.reprintKitchen.outcome.caisse.none',
  },
  cloud: {
    sent: 'waiter.ticket.commandes.reprintKitchen.outcome.cloud.sent',
    printed: 'waiter.ticket.commandes.reprintKitchen.outcome.cloud.printed',
    failed: 'waiter.ticket.commandes.reprintKitchen.outcome.cloud.failed',
    none: 'waiter.ticket.commandes.reprintKitchen.outcome.cloud.none',
  },
};

export function printOutcomeDestination(raw: string): PrintOutcomeDestination | null {
  return raw === 'clodop' || raw === 'caisse' || raw === 'cloud' ? raw : null;
}

export interface PrintOutcomePoller {
  fetchStatus: (dispatchUuid: string) => Promise<{ state: string; detail: string }>;
  announce: (message: string) => void;
  t: TFunction;
}

/**
 * Sonder la ligne d'attente jusqu'à ce qu'elle se prononce, puis l'annoncer.
 *
 * Le délai avant de conclure « personne n'écoute » appartient au mobile : le
 * serveur, lui, ne sait pas si un poste comptoir est ouvert. Un état qui ne
 * quitte jamais `waiting` au bout du délai est donc dit comme tel ; un
 * `printing` qui traîne ne dit rien, ce qui est volontaire — accuser une
 * imprimante lente renverrait un serveur vérifier un appareil qui marche.
 *
 * `messages` dit de QUEL papier il s'agit (défaut : l'addition, SC-1038 :
 * le bon de cuisine).
 */
export async function reportPrintOutcome(
  dispatchUuid: string,
  destination: PrintOutcomeDestination,
  { fetchStatus, announce, t }: PrintOutcomePoller,
  messages: PrintOutcomeMessages = PRINT_OUTCOME_MESSAGES[destination],
): Promise<void> {
  const deadline = Date.now() + PRINT_OUTCOME_TIMEOUT_MS[destination];
  let last: string | null = null;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, COUNTER_PRINT_POLL_MS));
    let status: { state: string; detail: string };
    try {
      status = await fetchStatus(dispatchUuid);
    } catch {
      continue;
    }
    last = status.state;
    if (status.state === 'printed') {
      announce(t(messages.printed));
      return;
    }
    if (status.state === 'failed') {
      announce(status.detail ? `${t(messages.failed)} (${status.detail})` : t(messages.failed));
      return;
    }
  }
  if (last === 'waiting') announce(t(messages.none));
}
